/* THE ONE RULE FOR A PERCENTAGE-OF-REVENUE VENDOR'S BASE (W-073 (b), 2026-09-30).
 *
 * A pct_revenue vendor's cost is rate x a revenue series, and vendors.pct_revenue_source says which. NULL used to mean BOTH
 * "chose Total Revenue" and "never answered", and both booked against total revenue, silently (measured on cid=3: "Merchant
 * Fees" 4% of Self Serve, blanked, moved forecast net income by -360,674.16 with the balance sheet balanced and no check
 * firing). A key naming no forecast revenue line booked $0, also silently.
 *
 * Brian 09-30: "Total Revenue" is an EXPLICIT stored choice -- PCT_REVENUE_TOTAL -- and a blank means "never answered":
 * refused at every save (the server) and flagged by the pre-flight (P18).
 *
 * Every reader and writer imports from here: computeVendor and the Vendor Dashboard's display overlay (the resolver), the
 * Vendor Modal, the bulk "Set % Source" and the Forecast Wizard (the sentinel), the picker's options and P18 (the base
 * keys). ACROSS THE WALL, server/server.js mirrors PCT_REVENUE_TOTAL and pctRevenueBaseKeys (pctSourceRefusal). */

import { miscLeafKey } from './miscKeys';
import { implTargetKey } from './implKeys';
import { indexGlByLeafKey, ownedLabelFor } from './leafLabels';

/* 2026-10-27 (Brian B1 / B2, diffs/pct-revenue-base-investigation.txt): "Total Revenue" MEANS THE INCOME STATEMENT'S TOTAL
 * REVENUE ROW -- the engine reads engines/revenueByLeaf.js's total, the same sum computeIS shows (it used to read AE + Company
 * Quota GAAP only, short by every misc and implementation line). And a specific-line base may be ANY revenue line a module
 * forecasts onto -- implementation lines and unrouted misc lines included -- read from the same revenue-by-line map. */

/** The stored value for "Total Revenue". The bulk control already used this exact string (then converted it to NULL). */
export const PCT_REVENUE_TOTAL = '__total_revenue__';
/** The option's label and its help text (one owner; every picker shows them). */
export const PCT_REVENUE_TOTAL_LABEL = 'Total Revenue';
export const PCT_REVENUE_TOTAL_HELP = "the Income Statement's Total Revenue";
/** The Vendor Modal's "% of Revenue" box (Brian B5): 0-100, at most two decimals, stored as a fraction -- utils/percentText.js's
 *  rule with these messages. */
export const PCT_RATE_MESSAGES = {
  required: 'Required — enter the percentage (0 for none)',
  range: '% of Revenue must be between 0% and 100%.',
  precision: '% of Revenue takes at most two decimals (e.g. 2.95).',
};

/** The revenue lines a vendor's percentage can apply to -- the lines a revenue module forecasts onto (a history-only line has
 *  no forecast, so a base on it would book $0):
 *    every revenue_module_mappings row that is not 'skip' (the AE / Company Quota targets and the misc targets -- the set the
 *      picker offered before 2026-10-27; every key offered then is offered now),
 *    each ENABLED misc line's key (its target, or its own rev_misc_<id> -- utils/miscKeys.js),
 *    each ENABLED implementation line's key (its target -- a Skip line included -- or its own rev_impl_<id> -- utils/implKeys.js).
 *  A NULL `enabled` is OFF, as in the engines (computeMiscRevenue / computeImplementationRevenue skip `!line.enabled`).
 *  Order: the rmm rows first (the old option order), then misc, then implementation; no key twice.
 *  ACROSS THE WALL: server/server.js pctRevenueBaseKeysFromRows is the same rule over the same three tables (pair M25). */
export function pctRevenueBaseKeys(revMappings, miscLines, implLines) {
  const keys = [];
  const add = k => { if (k && !keys.includes(k)) keys.push(k); };
  (revMappings || []).forEach(m => { if (m && m.module_type !== 'skip') add(m.gl_line_key); });
  (miscLines || []).forEach(l => { if (l && l.enabled) add(miscLeafKey(l)); });
  (implLines || []).forEach(l => { if (l && l.enabled) add(implTargetKey(l.id, l.target_gl_line_key)); });
  return keys;
}

/** The picker's options, [{ key, label }] in pctRevenueBaseKeys' order. A line's label is its own name from its owner
 *  (gl_line_mappings.display_name, utils/leafLabels.js); a line's own synthetic leaf (rev_misc_<id> / rev_impl_<id>), which no
 *  gl row owns, is labelled by its misc / implementation line's name; a key nothing names shows as the key. The "Total Revenue"
 *  option is not in this list -- each picker puts PCT_REVENUE_TOTAL first. */
export function pctRevenueBaseOptions({ revMappings, miscLines, implLines, glMappings } = {}) {
  const owners = indexGlByLeafKey(glMappings);
  const ownName = {};
  (miscLines || []).forEach(l => { if (l && l.enabled && !l.target_gl_line_key) ownName[miscLeafKey(l)] = l.name; });
  (implLines || []).forEach(l => { if (l && l.enabled) ownName[implTargetKey(l.id, l.target_gl_line_key)] = ownName[implTargetKey(l.id, l.target_gl_line_key)] || l.name; });
  return pctRevenueBaseKeys(revMappings, miscLines, implLines).map(key => ({ key, label: ownedLabelFor(owners, key) || ownName[key] || key }));
}

/** Resolve a stored source to the series it multiplies.
 *   the sentinel      -> { base: totalRevenue,    status: 'total' }
 *   NULL / ''         -> { base: totalRevenue,    status: 'unanswered' }  today's arithmetic, kept -- no number moves;
 *                                                                          the pre-flight (P18) names it
 *   a key byLine has  -> { base: byLine[key],     status: 'line' }
 *   any other key     -> { base: undefined,       status: 'unknown' }     today's $0, kept; P18 names it
 *  `byLine` / `totalRevenue` are engines/revenueByLeaf.js's byLeaf / total (computeVendor, its one caller since 2026-10-27; the
 *  Dashboard's display shows the engine's own per-vendor result instead of re-resolving). */
export function resolvePctRevenueBase(source, byLine, totalRevenue) {
  if (source === PCT_REVENUE_TOTAL) return { base: totalRevenue, status: 'total' };
  if (source == null || source === '') return { base: totalRevenue, status: 'unanswered' };
  const base = byLine ? byLine[source] : undefined;
  return base ? { base, status: 'line' } : { base: undefined, status: 'unknown' };
}
